"use client";

import { FiCalendar, FiClock, FiPhone } from "react-icons/fi";
import { useEffect } from "react";

interface ConfirmationMessageProps {
  serviceType: 'installation' | 'reparation';
  selectedService: string;
  serviceOption: string;
  selectedDate: Date | null;
  selectedTime: string | null;
  onClose: () => void;
  isMultipleServices?: boolean;
  multipleServices?: Array<{
    type: 'installation' | 'reparation';
    service: string;
    option: string;
  }>;
}

export default function ConfirmationMessage({
  serviceType,
  selectedService,
  serviceOption,
  selectedDate,
  selectedTime,
  onClose,
  isMultipleServices,
  multipleServices
}: ConfirmationMessageProps) {
  
  // Scroll automatique vers le haut quand le composant se monte
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 text-center">

      {/* Icône horloge — en attente, pas encore confirmé */}
      <div className="flex justify-center mb-4">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-primary/10 border-4 border-primary/20 flex items-center justify-center">
            <FiClock className="text-primary" size={40} />
          </div>
          <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping" style={{ animationDuration: "2s" }} />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-1">Demande reçue !</h2>
      <p className="text-gray-500 text-sm mb-5">
        Votre créneau est <span className="font-semibold text-amber-600">en attente de confirmation</span>.
      </p>

      {/* Bannière SMS */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-5 flex items-start gap-3 text-left">
        <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
          <FiPhone className="text-primary" size={18} />
        </div>
        <div>
          <p className="text-sm font-bold text-primary">Confirmation par SMS</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            Votre rendez-vous sera confirmé et vous recevrez un SMS de confirmation dans les plus brefs délais.
          </p>
        </div>
      </div>

      {/* Récapitulatif */}
      <div className="bg-gray-50 rounded-lg p-4 mb-5 text-left">
        <h3 className="font-semibold text-gray-800 mb-3 text-sm">Récapitulatif de votre demande</h3>

        {isMultipleServices && multipleServices ? (
          <div className="space-y-1.5">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Services demandés</p>
            {multipleServices.map((service, index) => (
              <div key={index} className="ml-2 text-sm text-gray-700">
                • {service.type === 'installation' ? 'Installation' : 'Réparation'} — {service.service} — {service.option}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1 text-sm text-gray-700">
            <p><strong>Service :</strong> {serviceType === 'installation' ? 'Installation' : 'Réparation'} — {selectedService}</p>
            <p><strong>Option :</strong> {serviceOption}</p>
          </div>
        )}

        {selectedDate && (
          <div className="flex items-center mt-3 text-sm text-gray-700">
            <FiCalendar className="mr-2 text-primary shrink-0" />
            <span>Date souhaitée : <strong>{formatDate(selectedDate)}</strong></span>
          </div>
        )}

        {selectedTime && (
          <div className="flex items-center mt-1.5 text-sm text-gray-700">
            <FiClock className="mr-2 text-primary shrink-0" />
            <span>Horaire : <strong>{selectedTime}</strong></span>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 mb-5">
        Besoin d'aide ? Appelez le{" "}
        <a href="tel:0658656990" className="font-semibold text-gray-500 hover:text-primary transition-colors">06 58 65 69 90</a>
      </p>

      <button
        onClick={() => {
          onClose();
          window.location.href = '/';
        }}
        className="bg-primary text-white py-3 px-8 rounded-md hover:bg-secondary transition-colors font-medium"
      >
        Retour à l'accueil
      </button>
    </div>
  );
} 